// src/context/PricingContext.tsx

import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../supabaseClient';
import { amplitudeApp } from '../index'; // Make sure this path is correct for your project structure

// --- INTERFACES (No changes here) ---

interface WalletPackage {
  id: string;
  amount: number;
  price: number;
  is_popular: boolean;
  is_first_recharge_offer?: boolean;
  display_order?: number;
}

// --- MODIFIED CONTEXT TYPE ---
interface PricingContextType {
  prices: Record<string, number>;
  packages: WalletPackage[];
  // First-recharge one-time offer (never-paid users only). null otherwise.
  firstRechargeOffer: WalletPackage | null;
  currencySymbol: string;
  formatPrice: (amount: number, options?: { maximumFractionDigits?: number }) => string;
  isLoading: boolean;
  variant: string; // <-- NEW: Expose the variant name for debugging or advanced logic
  showSubscriptions: boolean; // <-- NEW: The critical feature flag
  variantIsLoading: boolean;
}

const PricingContext = createContext<PricingContextType | undefined>(undefined);

// --- NEW: The flag key from your Amplitude dashboard ---
const MONETIZATION_FLAG_KEY = "pricingexperiment";
const CONTROL_VARIANT_NAME = 'control';

export const PricingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, currency, checkingStatus, pricingVariant: userVariantFromAuth } = useAuth();
  const [rawPrices, setRawPrices] = useState<Record<string, number>>({});
  // Per-service member prices (service_key → minor units) applied on top of the
  // standard prices once the user is an active Aura Plus member — so the chat
  // snackbar, rate pills etc. all reflect the discounted rate the shared backend
  // actually charges members. Null when not a member.
  const [memberPrices, setMemberPrices] = useState<Record<string, number> | null>(null);
  // Effective prices exposed to the app: standard, overridden by member rates.
  const prices = useMemo(
    () => (memberPrices ? { ...rawPrices, ...memberPrices } : rawPrices),
    [rawPrices, memberPrices],
  );
  const [packages, setPackages] = useState<WalletPackage[]>([]);
  const [firstRechargeOffer, setFirstRechargeOffer] = useState<WalletPackage | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [isLoading, setIsLoading] = useState(true);
  // const [variantIsLoading, setVariantIsLoading] = useState(true);

  // --- NEW: EXPERIMENT STATE ---
  const [variant, setVariant] = useState('control'); // Default to 'control' for safety
  const [showSubscriptions, setShowSubscriptions] = useState(true);
  const [variantIsLoading, setVariantIsLoading] = useState(true);
  // src/context/PricingContext.tsx

  // --- ACTION 2: REPLACE THE ENTIRE FIRST useEffect HOOK ---
  useEffect(() => {
    const determineVariant = async () => {
      setVariantIsLoading(true);
      if (user && userVariantFromAuth) {
        // CASE 1: USER IS LOGGED IN
        console.log(`[PricingContext] Received stable variant from AuthContext: ${userVariantFromAuth}`);
        setVariant(userVariantFromAuth);

        // --- ADD THIS LINE ---
        setShowSubscriptions(userVariantFromAuth === CONTROL_VARIANT_NAME);

      } else {
        // CASE 2: USER IS A GUEST
        try {
          console.log(`[PricingContext] Guest user detected. Fetching temporary variant from Amplitude.`);
          await amplitudeApp.experiment.fetch();
          const expVariant = amplitudeApp.experiment.variant(MONETIZATION_FLAG_KEY);
          const guestVariant = expVariant?.value || 'control';
          setVariant(guestVariant);
          sessionStorage.setItem('guestVariant', guestVariant);
          console.log(`[PricingContext] Guest assigned to temporary variant: ${guestVariant}`);

          // --- AND ADD THIS LINE ---
          setShowSubscriptions(guestVariant === CONTROL_VARIANT_NAME);

        } catch (e) {
          console.error("[PricingContext] Amplitude Experiment Fetch Error (Guest):", e);
          setVariant('control');
          // --- ALSO ADD A FALLBACK HERE ---
          setShowSubscriptions(false); // [MODIFIED] Force false
        }
      }
      setVariantIsLoading(false);
      // [NEW] Global override to ensure subscriptions are hidden everywhere
      setShowSubscriptions(false);
    };

    if (!checkingStatus) {
      determineVariant();
    }

  }, [user, checkingStatus, userVariantFromAuth]);

  // This useEffect for fetching pricing data is mostly the same, but now depends on the reactive `variant`
  useEffect(() => {
    if (!currency || !variant) {
      setIsLoading(false);
      return;
    }

    const fetchPricingData = async () => {
      setIsLoading(true);
      try {
        const { data: currData } = await supabase
          .from('supported_currencies')
          .select('symbol, decimal_places')
          .eq('code', currency)
          .single();

        if (currData) {
          setCurrencySymbol(currData.symbol);
          setDecimalPlaces(currData.decimal_places);
        }

        const { data: priceData, error: priceError } = await supabase
          .from('service_prices')
          .select('service_key, price_amount')
          .eq('currency_code', currency)
          .eq('variant_name', variant);

        if (priceError) throw priceError;
        if (priceData) {
          const priceMap: Record<string, number> = {};
          priceData.forEach(p => { priceMap[p.service_key] = p.price_amount; });
          setRawPrices(priceMap);
        }

        const { data: pkgData, error: pkgError } = await supabase
          .from('wallet_packages')
          .select('*')
          .eq('currency_code', currency)
          .eq('is_active', true)
          .eq('variant_name', variant)
          .order('display_order', { ascending: true });

        if (pkgError) throw pkgError;
        if (pkgData) setPackages(pkgData);

      } catch (error) {
        console.error('Error loading pricing data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricingData();
  }, [currency, variant]); // <-- This correctly re-runs when the variant changes after login

  // --- Member-aware pricing overlay ---
  // When the user is an active Aura Plus member, overlay the server-computed
  // per-service member rates onto the standard prices, so the chat snackbar,
  // rate pills and everywhere else show the discounted rate the backend actually
  // charges members (app parity). The authoritative source is
  // get_boot_context().membership.member_prices (computed via
  // fn_effective_service_price_minor from each member's frozen benefits
  // snapshot) — NOT get_subscription_offer.benefits, which is driven by the
  // often-empty subscription_benefits table. Non-members / failures fall back to
  // standard prices. Updates on next load after a purchase (SubscribeSheet calls
  // refreshUserStatus, changing `user`).
  useEffect(() => {
    let cancelled = false;
    const applyMemberPrices = async () => {
      if (!user) {
        if (!cancelled) setMemberPrices(null);
        return;
      }
      try {
        const { data: boot } = await supabase.rpc('get_boot_context', {
          p_user_id: user.id,
        });
        const mp = boot?.membership?.member_prices;
        if (
          boot?.membership?.is_member !== true ||
          !mp ||
          typeof mp !== 'object'
        ) {
          if (!cancelled) setMemberPrices(null);
          return;
        }
        const map: Record<string, number> = {};
        Object.entries(mp).forEach(([key, val]) => {
          if (typeof val === 'number') map[key] = val;
        });
        if (!cancelled) setMemberPrices(Object.keys(map).length ? map : null);
      } catch (e) {
        console.error('[pricing] member price overlay failed:', e);
        if (!cancelled) setMemberPrices(null);
      }
    };
    applyMemberPrices();
    return () => {
      cancelled = true;
    };
  }, [user, currency, variant]);

  // --- First-recharge one-time offer ---
  // Mirrors the app: the offer row lives in wallet_packages with
  // is_first_recharge_offer=true and is_active=false, so the normal grid query
  // (is_active=true) never picks it up. We fetch it EXPLICITLY here — by
  // currency only (one row serves all variants) — and show it ONLY to users who
  // have never had a succeeded payment. Failures are non-fatal (just no card).
  useEffect(() => {
    let cancelled = false;

    const fetchFirstRechargeOffer = async () => {
      if (!user || !currency) {
        setFirstRechargeOffer(null);
        return;
      }
      try {
        const { data: offerRows, error: offerErr } = await supabase
          .from('wallet_packages')
          .select('*')
          .eq('is_first_recharge_offer', true)
          .eq('currency_code', currency)
          .order('display_order', { ascending: true })
          .limit(1);

        if (offerErr) throw offerErr;
        if (!offerRows || offerRows.length === 0) {
          if (!cancelled) setFirstRechargeOffer(null);
          return;
        }

        // First-payment-only: hide the offer if the user has ANY succeeded payment.
        const { data: paid } = await supabase
          .from('payments')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'succeeded')
          .limit(1);

        const hasPaid = Array.isArray(paid) && paid.length > 0;
        if (!cancelled) {
          setFirstRechargeOffer(hasPaid ? null : (offerRows[0] as WalletPackage));
        }
      } catch (e) {
        console.error('Error loading first-recharge offer:', e);
        if (!cancelled) setFirstRechargeOffer(null);
      }
    };

    fetchFirstRechargeOffer();
    return () => {
      cancelled = true;
    };
  }, [user, currency]);

  // --- HELPER FUNCTION (No changes here) ---
  const formatPrice = (amount: number, options?: { maximumFractionDigits?: number }) => {
    if (amount === undefined || amount === null) return '...';
    const majorValue = amount / 100;

    // Member/discounted prices can be fractional (e.g. 50% off ₹15 = ₹7.5). The
    // currency's default decimalPlaces is 0 for INR, which would round ₹7.5 to
    // ₹8 — so when the value isn't whole, show up to 2 decimals instead.
    const isWhole = Number.isInteger(Math.round(majorValue * 100) / 100)
      ? Number.isInteger(majorValue)
      : false;
    const fractionDigits = options?.maximumFractionDigits !== undefined
      ? options.maximumFractionDigits
      : isWhole
        ? decimalPlaces
        : 2;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(majorValue);
  };

  // --- MODIFIED PROVIDER VALUE ---
  return (
    <PricingContext.Provider value={{ prices, packages, firstRechargeOffer, currencySymbol, formatPrice, isLoading, variant, showSubscriptions, variantIsLoading }}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (context === undefined) throw new Error('usePricing must be used within a PricingProvider');
  return context;
};